import React, { createContext, useContext, useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";

// Define Product interface (should match what's in Shop.tsx, ideally shared)
export interface Product {
    id: string;
    name: string;
    description?: string;
    price: number;
    originalPrice?: number;
    icon?: any; // Using any for icon component to avoid complex type imports for now
    iconBgColor?: string;
    iconTextColor?: string;
    badge?: string;
    features?: string[];
}

interface CartContextType {
    cart: Product[];
    addToCart: (product: Product) => void;
    removeFromCart: (productId: string) => void;
    clearCart: () => void;
    total: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cart, setCart] = useState<Product[]>(() => {
        const savedCart = localStorage.getItem('cart');
        return savedCart ? JSON.parse(savedCart) : [];
    });
    const { toast } = useToast();

    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cart));
    }, [cart]);

    const addToCart = (product: Product) => {
        if (cart.find(item => item.id === product.id)) {
            toast({ title: "Already in Cart", description: `${product.name} is already in your cart.` });
            return;
        }
        setCart((prev) => [...prev, product]);
        toast({
            title: "Added to Cart!",
            description: `${product.name} is now in your cart.`,
        });
    };

    const removeFromCart = (productId: string) => {
        setCart((prev) => prev.filter(item => item.id !== productId));
    };

    const clearCart = () => {
        setCart([]);
        toast({ title: "Cart Cleared", description: "Your shopping cart has been emptied.", variant: "destructive" });
    };

    const total = cart.reduce((sum, item) => sum + item.price, 0);

    return (
        <CartContext.Provider value={{ cart, addToCart, removeFromCart, clearCart, total }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
